import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ShieldCheck, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/data/site";
import { OrayaWordmark } from "@/components/site/primitives";
import { OButton } from "@/components/site/OButton";
import { ThemeToggle } from "@/components/site/ThemeToggle";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const loginRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setLoginOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => { if (loginRef.current && !loginRef.current.contains(e.target)) setLoginOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav border-b border-hairline/[0.05] py-3" : "py-5 bg-transparent"}`}
      >
        <div className="oraya-container flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="ORAYA home">
            <OrayaWordmark />
          </Link>

          <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-sm tracking-wide transition-colors duration-300 ${location.pathname.startsWith(l.to) ? "text-ink" : "text-subtle hover:text-ink"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <div className="relative" ref={loginRef}>
              <button
                onClick={() => setLoginOpen((v) => !v)}
                data-testid="nav-login-btn"
                className="flex items-center gap-1.5 text-sm tracking-wide text-subtle hover:text-ink transition-colors duration-300 px-2 py-1"
              >
                Login <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${loginOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {loginOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-3 w-64 glass-nav border border-hairline/[0.08] rounded-2xl p-2 shadow-xl"
                    data-testid="nav-login-menu"
                  >
                    <Link to="/admin" data-testid="nav-login-admin" className="flex items-start gap-3 p-3 rounded-xl hover:bg-ink/[0.04] transition-colors">
                      <ShieldCheck className="w-5 h-5 text-subtle mt-0.5" />
                      <span><span className="block text-sm text-ink">Admin Portal</span><span className="block text-xs text-subtle mt-0.5">Website & content management</span></span>
                    </Link>
                    <Link to="/command/login" data-testid="nav-login-founder" className="flex items-start gap-3 p-3 rounded-xl hover:bg-ink/[0.04] transition-colors">
                      <LayoutDashboard className="w-5 h-5 text-subtle mt-0.5" />
                      <span><span className="block text-sm text-ink">Founder Portal</span><span className="block text-xs text-subtle mt-0.5">Command Center</span></span>
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <OButton to="/contact" variant="secondary" data-testid="nav-contact-btn">Contact</OButton>
          </div>

          <button
            className="md:hidden text-ink p-2"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 md:hidden glass-nav flex flex-col pt-28 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {NAV_LINKS.concat([{ label: "Careers", to: "/careers" }, { label: "Contact", to: "/contact" }]).map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="block display text-4xl font-light text-ink py-3 border-b border-hairline/[0.06]"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-login-admin"
                className="flex items-center gap-3 glass-medium rounded-xl p-4 text-ink">
                <ShieldCheck className="w-5 h-5 text-subtle" /> <span className="text-base">Admin Portal</span>
              </Link>
              <Link to="/command/login" onClick={() => setOpen(false)} data-testid="mobile-login-founder"
                className="flex items-center gap-3 glass-medium rounded-xl p-4 text-ink">
                <LayoutDashboard className="w-5 h-5 text-subtle" /> <span className="text-base">Founder Portal</span>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-3 text-subtle">
              <ThemeToggle />
              <span className="text-sm">Switch theme</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
